import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  signIn,
  signOut,
  getCurrentUser,
  confirmSignIn,
  signInWithRedirect,
  fetchAuthSession,
  signUp as amplifySignUp,
  confirmSignUp as amplifyConfirmSignUp,
  AuthUser,
  SignInOutput
} from 'aws-amplify/auth';
import { initializeAuth, initializeSession } from '../services/auth';
import { useSession } from './SessionContext';

interface AuthContextType {
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  user: AuthUser | SignInOutput | null;
  login: (username: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  signup: (email: string, password: string) => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  completeNewPassword: (newPassword: string) => Promise<void>;
  needsPasswordChange: boolean;
  authError: string | null;
  clearAuthError: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<AuthUser | SignInOutput | null>(null);
  const [needsPasswordChange, setNeedsPasswordChange] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const { setSession } = useSession();

  useEffect(() => {
    checkAuthState();
    checkOAuthError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty - only run on mount

  const checkOAuthError = () => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    const errorDescription = params.get('error_description');

    if (error && errorDescription) {
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);

      // Set user-friendly error message
      if (errorDescription.includes('not authorized to access this application')) {
        setAuthError('Your email is not authorized to access this application. Please contact an administrator.');
      } else {
        setAuthError(errorDescription.replace(/\+/g, ' '));
      }
    }
  };

  const checkAdminStatus = async () => {
    try {
      const session = await fetchAuthSession();
      const groups = (session.tokens?.idToken?.payload?.['cognito:groups'] as string[] | undefined) || [];
      setIsAdmin(groups.includes('Admins'));
    } catch {
      setIsAdmin(false);
    }
  };

  const checkAuthState = async () => {
    try {
      const user = await getCurrentUser();
      const sessionData = await initializeSession();

      // Initialize OpenAPI authentication for all authenticated users
      initializeAuth();
      await checkAdminStatus();

      if (sessionData.onboarding_required) {
        // New user needs onboarding - set session data which will trigger onboarding flow
        setSession(sessionData);
        setUser(user);
        setIsAuthenticated(true);
        setAuthError(null);
      } else {
        // Existing user - standard login flow
        setSession(sessionData);
        setUser(user);
        setIsAuthenticated(true);
        setAuthError(null);
      }
    } catch (error) {
      setIsAuthenticated(false);
      setIsAdmin(false);
      setUser(null);
      // Mark session as initialized even on error to prevent infinite loading
      setSession({ onboarding_required: false });
    } finally {
      setIsLoading(false);
    }
  };

  const signup = async (email: string, password: string) => {
    try {
      await amplifySignUp({
        username: email,
        password,
        options: { userAttributes: { email } },
      });
    } catch (error: any) {
      console.error('Signup error:', error);
      throw new Error(error.message || 'Signup failed');
    }
  };

  const confirmSignUpCode = async (email: string, code: string) => {
    try {
      await amplifyConfirmSignUp({ username: email, confirmationCode: code });
    } catch (error: any) {
      console.error('Confirm signup error:', error);
      throw new Error(error.message || 'Verification failed');
    }
  };

  const loginWithGoogle = async () => {
    try {
      console.log('🔧 AuthContext: Starting Google OAuth flow...');

      // If already signed in, sign out first to allow fresh OAuth flow
      try {
        const currentUser = await getCurrentUser();
        if (currentUser) {
          console.log('🔧 AuthContext: User already signed in, signing out first...');
          await signOut();
        }
      } catch (error) {
        // No user signed in, proceed with OAuth
        console.log('🔧 AuthContext: No existing session, proceeding with OAuth');
      }

      await signInWithRedirect({ provider: 'Google' });
      // User will be redirected to Google, then back to app
    } catch (error: any) {
      console.error('❌ AuthContext: Google login error:', error);
      throw new Error(error.message || 'Google login failed');
    }
  };

  const login = async (username: string, password: string) => {
    try {
      console.log('🔧 AuthContext: Starting login with timeout...');

      // If already signed in (stale session), sign out first to avoid
      // "There is already a signed in user" error
      try {
        const currentUser = await getCurrentUser();
        if (currentUser) {
          console.log('🔧 AuthContext: Stale session detected, signing out first...');
          await signOut();
        }
      } catch {
        // No user signed in, proceed
      }

      // Add timeout to prevent infinite hanging - increased to 45 seconds for slow connections
      const signInPromise = signIn({ username, password });
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Login timeout after 45 seconds')), 45000);
      });

      const signInResult = await Promise.race([signInPromise, timeoutPromise]) as SignInOutput;
      console.log('✅ AuthContext: SignIn completed:', signInResult);

      if (signInResult.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        console.log('🔑 AuthContext: Password change required');
        setUser(signInResult);
        setNeedsPasswordChange(true);
        return;
      }

      console.log('🎉 AuthContext: Login successful, setting authenticated state');
      setUser(signInResult);
      setIsAuthenticated(true);
      setNeedsPasswordChange(false);
      // Initialize OpenAPI authentication for all authenticated users
      initializeAuth();
      await checkAdminStatus();
      const sessionData = await initializeSession();
      setSession(sessionData); // Always call setSession to mark as initialized
    } catch (error: any) {
      console.error('❌ AuthContext: Login error:', error);
      throw new Error(error.message || 'Login failed');
    }
  };

  const completeNewPassword = async (newPassword: string) => {
    try {
      const confirmResult = await confirmSignIn({
        challengeResponse: newPassword
      });
      setUser(confirmResult);
      setIsAuthenticated(true);
      setNeedsPasswordChange(false);
      // Initialize OpenAPI authentication for all authenticated users
      initializeAuth();
      await checkAdminStatus();
      const sessionData = await initializeSession();
      setSession(sessionData); // Always call setSession to mark as initialized
    } catch (error: any) {
      throw new Error(error.message || 'Password change failed');
    }
  };

  const logout = async () => {
    try {
      await signOut();
      setUser(null);
      setIsAuthenticated(false);
      setIsAdmin(false);
      setNeedsPasswordChange(false);
      setAuthError(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const clearAuthError = () => {
    setAuthError(null);
  };

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      isAdmin,
      isLoading,
      user,
      login,
      loginWithGoogle,
      logout,
      signup,
      confirmSignUp: confirmSignUpCode,
      completeNewPassword,
      needsPasswordChange,
      authError,
      clearAuthError
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};